import React, { useState } from 'react';
import { ReactComponent as ArrowExternalIcon } from '../icons/arrow-external-icon.svg';
import { ReactComponent as OccasiaLogoIcon } from '../icons/occasia-logo-icon.svg';
import { ReactComponent as InstagramIcon } from '../icons/instagram-icon.svg';
import { ReactComponent as LinkedInIcon } from '../icons/linkedin-icon.svg';
import { API_BASE_URL } from '../../config/apiEndpoints';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitStatus(null);

    const requestBody = {
      email: email.trim(),
      update_enabled: false,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (response.ok) {
        setSubmitStatus('success');
        setEmail("");
      } else {
        // Only show error if it's not a validation error from the API
        const errorMessage = data?.message || response.statusText || 'Subscription failed';
        if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('invalid')) {
          setSubmitStatus('error');
        } else {
          // For other errors, show a generic message
          setSubmitStatus('error');
        }
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#e6ecd6] flex flex-col gap-12 md:gap-20 lg:gap-[120px] px-6 py-12 md:py-20">
      {/* Newsletter Section */}
      <div className="content-width mx-auto bg-white flex flex-col gap-10 p-6 md:p-12 lg:p-20 w-full">
        <div className="flex flex-col gap-4">
          <h2 className="font-playfair font-medium text-3xl md:text-[40px] text-black md:whitespace-pre">
            Be the First to Know
          </h2>
          <p className="font-inter font-normal text-base md:text-xl leading-relaxed text-black max-w-none md:max-w-[800px]">
            Join our mailing list for curated event recommendations from our vetted venues, delivered straight to your inbox.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row md:gap-10 gap-6 items-end w-full" noValidate>
          <div className="flex flex-col gap-2 flex-1 w-full">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              onBlur={(e) => {
              }}
              className="font-inter font-normal text-base md:text-xl bg-transparent border-none outline-none placeholder:text-[rgba(10,50,38,0.2)]"
              disabled={isSubmitting || submitStatus === 'success'}
              required
            />
            <div className="bg-[#0a3226] h-px opacity-20 w-full" />
            {submitStatus === 'error' && (
              <p className="font-inter font-normal text-sm text-red-600 mt-1">
                Subscription failed. Please try again.
              </p>
            )}
            {submitStatus === 'success' && (
              <p className="font-inter font-normal text-sm text-[#046242] mt-1">
                Subscribed successfully!
              </p>
            )}
          </div>
          <button
            type="submit"
            className={`bg-[#046242] flex items-center justify-center gap-2 px-6 py-4 rounded-lg shrink-0 w-full md:w-auto ${
              submitStatus === 'success' ? 'opacity-75' : ''
            }`}
            disabled={isSubmitting || submitStatus === 'success'}
          >
            <span className="font-inter font-medium text-base md:text-xl text-white whitespace-pre">
              {isSubmitting ? 'Subscribing...' : submitStatus === 'success' ? 'Subscribed!' : 'Subscribe'}
            </span>
            {submitStatus !== 'success' && <ArrowExternalIcon className="text-white" />}
          </button>
        </form>
      </div>

      {/* Footer Content */}
      <div className="content-width mx-auto flex flex-col gap-12 lg:gap-12 w-full">
        <div className="grid md:grid-cols-2 grid-cols-1 md:items-start md:justify-between gap-12">
          {/* Left Column */}
          <div className="flex flex-col gap-8 md:gap-0 md:justify-between lg:min-h-[240px]">
            <div className="flex flex-col gap-4">
              <div className="h-6 w-[119px]">
                <OccasiaLogoIcon className="w-full h-full text-black" />
              </div>
              <p className="font-inter font-normal text-base leading-relaxed text-[#6f6f6f]">
                Connecting communities through unique spaces
              </p>
            </div>
            
            
          </div>

          {/* Right Column */}
            {/* Navigation Links */}
            <div className="flex flex-col md:flex-row flex-wrap gap-10 md:justify-end lg:justify-start">
              <a href="/about" className="font-inter font-medium text-base md:text-xl leading-relaxed text-[#046242] md:w-[96px] hover:underline">
                About Us
              </a>
              <a href="/" className="font-inter font-medium text-base md:text-xl leading-relaxed text-[#046242] whitespace-pre hover:underline">
                For Event Organisers
              </a>
              <a href="/onboard?persona=venues" className="font-inter font-medium text-base md:text-xl leading-relaxed text-[#046242] whitespace-pre hover:underline">
                For Venues
              </a>
            </div>

        </div>
        <div className='grid md:grid-cols-2 grid-cols-1 gap-12 flex-row-reverse justify-between'>
                  {/* Legal Links */}
        <div className="flex flex-col gap-4 h-20 order-2 md:order-1">
          <div className="flex gap-2 items-start text-nowrap">
            <a href="/terms" className="font-inter font-normal text-sm text-[#6f6f6f] hover:underline">
              Terms of Use
            </a>
            <span className="font-inter font-normal text-sm text-[#6f6f6f]">•</span>
            <a href="/privacy" className="font-inter font-normal text-sm text-[#6f6f6f] hover:underline">
              Privacy Policy
            </a>
          </div>
          <p className="font-inter font-normal text-sm text-[#6f6f6f] max-w-[420px]">
            ©2025 Copyright
          </p>
        </div>

                    {/* Follow Us & Contact Us */}
                    <div className="grid grid-cols-2 gap-12 order-1 md:order-2">
              <div className="flex flex-col gap-4">
                <h3 className="font-inter font-medium text-base md:text-xl leading-[1.1] tracking-[-0.16px] md:tracking-[-0.2px] text-black whitespace-pre">
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/occasia.ofcl/" className="bg-white flex items-center justify-center rounded-full w-10 h-10">
                    <InstagramIcon className="w-[18px] h-[18px]" style={{ color: '#046242' }} />
                  </a>
                  <a href="https://www.linkedin.com/company/occasia/about/" className="bg-white flex items-center justify-center rounded-full w-10 h-10">
                    <LinkedInIcon className="w-[18px] h-[18px]" />
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="font-inter font-medium text-base md:text-xl leading-[1.1] tracking-[-0.16px] md:tracking-[-0.2px] text-black whitespace-pre">
                  Contact Us
                </h3>
                <div className="flex flex-col font-inter font-normal text-sm leading-relaxed text-[#6f6f6f]">
                  {/* <p className="whitespace-pre">+1 (999) 888-77-66</p> */}
                  <p className="">support@occasia.io</p>
                </div>
              </div>
            </div>


        </div>
      </div>
    </div>
  );
};

export default Footer;
